import useAuthStore from "../../store/auth.store";
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  myOrganisation,
  inviteUser,
  updateUserRole,
  removeUser,
  resendInvite,
  revokeInvite,
} from "../../api/organisations.api";

// Display labels for the v2 role codes (matches the invite dropdown).
const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  ADMIN: "Admin",
  USER: "User",
};

// Legacy User.role -> v2 code (mirrors backend V2_TO_LEGACY).
const LEGACY_TO_V2 = { OWNER: "SUPER_ADMIN", ADMIN: "ADMIN", MEMBER: "USER" };
const toV2Role = (legacyOrV2) =>
  LEGACY_TO_V2[legacyOrV2?.toUpperCase?.()] || legacyOrV2?.toUpperCase?.() || "USER";

export default function OrganisationDashboard() {
  const [orgData, setOrgData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const invitePanelRef = useRef(null);
  useDialogDismiss({
    open: showInviteModal,
    onClose: () => setShowInviteModal(false),
    ref: invitePanelRef,
  });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("USER");
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Per-row action state — { kind: "role"|"remove"|"resend"|"revoke", id, busy, error }
  const [rowAction, setRowAction] = useState({});
  const [toast, setToast] = useState(null); // { ok: bool, msg }

  const { user, organizationName } = useAuthStore();

  const flashToast = (ok, msg) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const refreshOrg = () =>
    myOrganisation()
      .then((res) => setOrgData(res.data))
      .catch(() => {});

  const setBusy = (key, busy) =>
    setRowAction((prev) => ({ ...prev, [key]: busy }));

  // Fetch organisation
  useEffect(() => {
    myOrganisation()
      .then((res) => {
        setOrgData(res.data);
      })
      .catch((err) => {
        setError(err.response?.data?.message || "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ---------- Error Handling ----------
  if (error === "no organisation found") {
    return <Navigate to="/create-organization" />;
  }

  if (error === "you dont have permission to view") {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center p-4">
        <div className="bg-bg-primary rounded-2xl p-8 border border-red-500/30 max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-text-primary text-center mb-2">
            Access Denied
          </h2>
          <p className="text-text-secondary text-center">
            You don't have permission to view this organization.
          </p>
        </div>
      </div>
    );
  }

  if (loading || !orgData) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading organization...</p>
        </div>
      </div>
    );
  }

  const { organization, stats, users, invites } = orgData;

  // ---------- Invite handler ----------
  const handleInvite = () => {
    setInviteError(null);
    setInviteLink(null);
    setInviteLoading(true);
    setLinkCopied(false);

    inviteUser({
      email: inviteEmail,
      role: inviteRole,
    })
      .then((res) => {
        const token = res.data.invite.token;
        setInviteLink(`${window.location.origin}/accept-invite/${token}`);

        // Refresh organization data to show new invite
        myOrganisation().then((res) => setOrgData(res.data));
      })
      .catch((err) => {
        setInviteError(
          err.response?.data?.email?.[0] ||
            err.response?.data?.detail ||
            "Failed to create invite",
        );
      })
      .finally(() => {
        setInviteLoading(false);
      });
  };

  const closeModal = () => {
    setShowInviteModal(false);
    setInviteEmail("");
    setInviteRole("USER");
    setInviteLink(null);
    setInviteError(null);
    setLinkCopied(false);
  };

  const copyToClipboard = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // ---------- User row actions ----------
  const handleRoleChange = async (u, newRole) => {
    if (toV2Role(u.role) === newRole) return;
    const key = `role-${u.id}`;
    setBusy(key, true);
    try {
      await updateUserRole(u.id, newRole);
      flashToast(true, `${u.email} is now ${ROLE_LABELS[newRole]}`);
      await refreshOrg();
    } catch (err) {
      flashToast(false, err.response?.data?.detail || err.response?.data?.message || "Role change failed");
    } finally {
      setBusy(key, false);
    }
  };

  const handleRemoveUser = async (u) => {
    if (!window.confirm(`Remove ${u.first_name} ${u.last_name} (${u.email}) from the organization? Their authored data stays intact.`)) return;
    const key = `remove-${u.id}`;
    setBusy(key, true);
    try {
      await removeUser(u.id);
      flashToast(true, `${u.email} removed`);
      await refreshOrg();
    } catch (err) {
      flashToast(false, err.response?.data?.detail || err.response?.data?.message || "Remove failed");
    } finally {
      setBusy(key, false);
    }
  };

  // ---------- Invite row actions ----------
  const handleResendInvite = async (inv) => {
    const key = `resend-${inv.id}`;
    setBusy(key, true);
    try {
      const res = await resendInvite(inv.id);
      flashToast(res.data?.email_sent !== false, res.data?.message || `Resent to ${inv.email}`);
    } catch (err) {
      flashToast(false, err.response?.data?.message || err.response?.data?.detail || "Resend failed");
    } finally {
      setBusy(key, false);
    }
  };

  const handleRevokeInvite = async (inv) => {
    if (!window.confirm(`Revoke the invite for ${inv.email}? The accept link will stop working.`)) return;
    const key = `revoke-${inv.id}`;
    setBusy(key, true);
    try {
      await revokeInvite(inv.id);
      flashToast(true, `Invite for ${inv.email} revoked`);
      await refreshOrg();
    } catch (err) {
      flashToast(false, err.response?.data?.detail || err.response?.data?.message || "Revoke failed");
    } finally {
      setBusy(key, false);
    }
  };

  // Role badge colors
  const getRoleBadgeColor = (role) => {
    switch (role?.toUpperCase()) {
      case "OWNER":
      case "SUPER_ADMIN":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "ADMIN":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "MEMBER":
      case "USER":
        return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      default:
        return "bg-bg-secondary text-text-secondary border-bg-secondary";
    }
  };

  // Status badge colors
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "pending":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "expired":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-bg-secondary text-text-secondary border-bg-secondary";
    }
  };

  return (
    <div className="min-h-screen bg-bg-main py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Toast (action feedback) */}
        {toast && (
          <div
            className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium ${
              toast.ok
                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                : "bg-red-500/15 border-red-500/40 text-red-300"
            }`}
          >
            {toast.msg}
          </div>
        )}
        {/* Page Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-text-primary mb-2">
              Organization Dashboard
            </h1>
            <p className="text-text-secondary">
              Manage your team and organization settings
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-3 bg-button-primary text-text-primary font-medium rounded-lg hover:opacity-90 transition-all duration-200 shadow-lg flex items-center gap-2"
            style={{ backgroundColor: "#6B1991" }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Invite User
          </button>
        </div>

        {/* Organization Info Card */}
        <div className="bg-bg-primary rounded-2xl p-8 mb-6 border border-bg-secondary shadow-lg">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-text-primary mb-1">
                {organization.name}
              </h2>
              <p className="text-text-secondary text-sm">
                Created on{" "}
                {new Date(organization.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl flex items-center justify-center border border-purple-500/30">
              <svg
                className="w-8 h-8 text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-bg-main rounded-lg p-6 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">
                    Total Members
                  </p>
                  <p className="text-3xl font-bold text-text-primary">
                    {stats.total_users}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-bg-main rounded-lg p-6 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">
                    Active Invites
                  </p>
                  <p className="text-3xl font-bold text-text-primary">
                    {
                      invites.filter(
                        (inv) => inv.status?.toLowerCase() === "pending",
                      ).length
                    }
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-bg-main rounded-lg p-6 border border-bg-secondary">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary mb-1">
                    Total Invites
                  </p>
                  <p className="text-3xl font-bold text-text-primary">
                    {invites.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Users Section */}
          <div className="bg-bg-primary rounded-2xl p-8 border border-bg-secondary shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-text-primary">
                Team Members
              </h2>
              <span className="px-3 py-1 bg-bg-secondary rounded-full text-sm text-text-secondary">
                {users.length} {users.length === 1 ? "member" : "members"}
              </span>
            </div>

            {users.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-text-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <p className="text-text-secondary">No users found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {users.map((u) => {
                  const isSelf = user?.id === u.id;
                  const isOwner = u.role === "OWNER";
                  const v2 = toV2Role(u.role);
                  const roleBusy = rowAction[`role-${u.id}`];
                  const removeBusy = rowAction[`remove-${u.id}`];
                  const locked = isSelf || isOwner;
                  return (
                    <div
                      key={u.id}
                      className="bg-bg-main rounded-lg p-4 border border-bg-secondary hover:border-button-primary/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500/30 to-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-text-primary font-semibold text-sm">
                              {u.first_name?.[0]}
                              {u.last_name?.[0]}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-text-primary font-medium truncate">
                              {u.first_name} {u.last_name}
                              {isSelf && <span className="ml-2 text-xs text-text-secondary">(you)</span>}
                            </p>
                            <p className="text-text-secondary text-sm truncate">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {locked ? (
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}
                              title={isOwner ? "Owner role cannot be modified here" : "You cannot change your own role"}
                            >
                              {ROLE_LABELS[v2] || u.role}
                            </span>
                          ) : (
                            <select
                              value={v2}
                              disabled={roleBusy}
                              onChange={(e) => handleRoleChange(u, e.target.value)}
                              className={`px-2 py-1 rounded text-xs font-medium border bg-bg-secondary text-text-primary outline-none focus:border-button-primary ${roleBusy ? "opacity-60" : ""}`}
                              title="Change this user's role"
                            >
                              <option value="SUPER_ADMIN">Super Admin</option>
                              <option value="ADMIN">Admin</option>
                              <option value="USER">User</option>
                            </select>
                          )}
                          {!locked && (
                            <button
                              onClick={() => handleRemoveUser(u)}
                              disabled={removeBusy}
                              className="px-2 py-1 rounded text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                              title="Remove from organization"
                            >
                              {removeBusy ? "…" : "Remove"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Invites Section */}
          <div className="bg-bg-primary rounded-2xl p-8 border border-bg-secondary shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-text-primary">
                Invitations
              </h2>
              <span className="px-3 py-1 bg-bg-secondary rounded-full text-sm text-text-secondary">
                {invites.length} {invites.length === 1 ? "invite" : "invites"}
              </span>
            </div>

            {invites.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-text-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-text-secondary">No invites created</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {invites.map((invite) => {
                  const isPending = invite.status?.toLowerCase() === "pending";
                  const resendBusy = rowAction[`resend-${invite.id}`];
                  const revokeBusy = rowAction[`revoke-${invite.id}`];
                  return (
                    <div
                      key={invite.id}
                      className="bg-bg-main rounded-lg p-4 border border-bg-secondary"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary font-medium mb-1 truncate">
                            {invite.email}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(invite.role)}`}
                            >
                              {ROLE_LABELS[invite.role] || invite.role}
                            </span>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadgeColor(invite.status)}`}
                            >
                              {invite.status}
                            </span>
                          </div>
                        </div>
                        {isPending && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleResendInvite(invite)}
                              disabled={resendBusy}
                              className="px-2 py-1 rounded text-xs border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 disabled:opacity-50"
                              title="Resend invitation email"
                            >
                              {resendBusy ? "…" : "Resend"}
                            </button>
                            <button
                              onClick={() => handleRevokeInvite(invite)}
                              disabled={revokeBusy}
                              className="px-2 py-1 rounded text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                              title="Revoke invitation"
                            >
                              {revokeBusy ? "…" : "Revoke"}
                            </button>
                          </div>
                        )}
                      </div>

                      {invite.accepted_user && (
                        <div className="mt-3 pt-3 border-t border-bg-secondary">
                          <p className="text-text-secondary text-xs mb-1">
                            Accepted by:
                          </p>
                          <p className="text-text-primary text-sm">
                            {invite.accepted_user.first_name}{" "}
                            {invite.accepted_user.last_name}
                            <span className="text-text-secondary">
                              {" "}
                              ({invite.accepted_user.email})
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) setShowInviteModal(false); }}
        >
          <div
            ref={invitePanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Invite member"
            className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-bg-secondary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-text-primary">
                    Invite Team Member
                  </h3>
                </div>
                <button
                  onClick={closeModal}
                  className="text-text-secondary hover:text-text-primary transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {!inviteLink ? (
                <>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                      placeholder="colleague@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-text-secondary mb-2">
                      Role <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                    >
                      <option value="USER">User — data entry &amp; validation</option>
                      <option value="ADMIN">Admin — all data ops + invite users</option>
                      <option value="SUPER_ADMIN">Super Admin — billing + full admin</option>
                    </select>
                  </div>

                  {inviteError && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm">{inviteError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleInvite}
                      disabled={inviteLoading || !inviteEmail}
                      className="flex-1 py-3 px-6 bg-button-primary text-text-primary font-medium rounded-lg hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: "#6B1991" }}
                    >
                      {inviteLoading ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                            />
                          </svg>
                          Generate Invite Link
                        </>
                      )}
                    </button>
                    <button
                      onClick={closeModal}
                      className="px-6 py-3 bg-bg-secondary text-text-secondary font-medium rounded-lg hover:bg-bg-main transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-emerald-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-text-primary mb-2">
                      Invite Link Generated!
                    </h4>
                    <p className="text-text-secondary text-sm mb-4">
                      Share this link with {inviteEmail}
                    </p>
                  </div>

                  <div className="bg-bg-main rounded-lg p-4 border border-bg-secondary">
                    <p className="text-text-secondary text-xs mb-2">
                      Invitation Link
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="flex-1 p-2 px-3 rounded bg-bg-secondary text-text-primary text-sm border border-bg-secondary outline-none"
                      />
                      <button
                        onClick={copyToClipboard}
                        className="px-4 py-2 bg-button-primary text-text-primary rounded-lg hover:opacity-90 transition-all flex items-center gap-2"
                        style={{ backgroundColor: "#6B1991" }}
                      >
                        {linkCopied ? (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={closeModal}
                    className="w-full py-3 px-6 bg-bg-secondary text-text-primary font-medium rounded-lg hover:bg-bg-main transition-colors"
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
